import React, { useRef, useState } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	IconButton,
	LinearProgress,
	Tooltip,
} from '@mui/material';
import styled from 'styled-components';
import { Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { Spacer } from '@Styled/utilities';
import { uploadPicture } from '@Services/uploads/uploads.req';
import { useSnackbar } from '@Providers/useSnackbar';

type Props = {
	label?: string;
	onUploaded: (uri: string) => void;
	/** Render as a small icon button (tooltip = label) instead of a full button. */
	iconOnly?: boolean;
	/**
	 * Lock the upload to this aspect ratio (e.g. 16 / 5 for the landing hero):
	 * the file opens in a cropper and the crop is saved as WebP at its native
	 * resolution. The student portals size their boxes to each slot's ratio,
	 * so an unlocked ratio is what makes covers crop. GIFs are refused in this
	 * mode — the canvas export would silently freeze the animation.
	 */
	cropRatio?: number;
};

/**
 * Image upload straight to S3 via the photo upload endpoint. Without
 * `cropRatio` there is no crop step and no re-encode — branding assets
 * (logos, favicons) must keep their original bytes.
 */
const AssetUploadButton: React.FC<Props> = ({
	label = 'Upload',
	onUploaded,
	iconOnly,
	cropRatio,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const cropperRef = useRef<CropperRef>();
	const [uploading, setUploading] = useState(false);
	const [cropSrc, setCropSrc] = useState<string>();
	const { displaySnackbar } = useSnackbar();

	const showError = (error: any) => {
		const message =
			error?.response?.data?.errors?.[0]?.message ??
			error?.response?.data?.message ??
			'Unable to upload image';
		displaySnackbar('error', message);
	};

	const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		if (cropRatio) {
			const reader = new FileReader();
			reader.onload = () => setCropSrc(reader.result as string);
			reader.readAsDataURL(file);
			return;
		}
		setUploading(true);
		try {
			const {
				data: { data },
			} = await uploadPicture({ photo: file, folder: 'assets' });
			onUploaded(data.uri);
		} catch (error: any) {
			showError(error);
		} finally {
			setUploading(false);
		}
	};

	const onCropSave = async () => {
		setUploading(true);
		try {
			const canvas = cropperRef.current?.getCanvas();
			if (!canvas) throw new Error('Unable to prepare the selected image');
			const blob = await new Promise<Blob>((resolve, reject) => {
				canvas.toBlob(
					(result) =>
						result
							? resolve(result)
							: reject(new Error('Unable to process the selected image')),
					'image/webp',
					0.8
				);
			});
			const {
				data: { data },
			} = await uploadPicture({ photo: blob, folder: 'assets' });
			onUploaded(data.uri);
			setCropSrc(undefined);
		} catch (error: any) {
			showError(error);
		} finally {
			setUploading(false);
		}
	};

	return (
		<>
			<input
				ref={inputRef}
				type="file"
				accept={
					cropRatio
						? 'image/png,image/jpeg,image/webp'
						: 'image/png,image/jpeg,image/webp,image/gif'
				}
				hidden
				onChange={onFileChange}
			/>
			{iconOnly ? (
				<Tooltip title={uploading ? 'Uploading...' : label}>
					<span>
						<IconButton
							size="small"
							disabled={uploading}
							onClick={() => inputRef.current?.click()}
						>
							{uploading ? (
								<CircularProgress size={16} />
							) : (
								<UploadFileIcon fontSize="small" />
							)}
						</IconButton>
					</span>
				</Tooltip>
			) : (
				<EdButton
					variant="outlined"
					edcolor="purple"
					startIcon={<UploadFileIcon />}
					disabled={uploading}
					onClick={() => inputRef.current?.click()}
				>
					{uploading ? 'Uploading...' : label}
				</EdButton>
			)}
			{cropRatio && (
				<Dialog
					open={!!cropSrc}
					onBackdropClick={() => !uploading && setCropSrc(undefined)}
				>
					<CropDialogWrapper>
						{uploading && <LinearProgress />}
						<Cropper
							stencilProps={{ aspectRatio: cropRatio }}
							src={cropSrc}
							className="cropper"
							ref={cropperRef}
						/>
						<Spacer my="0.938rem" />
						<DialogActions>
							<EdButton
								disabled={uploading}
								onClick={onCropSave}
								variant="text"
								edcolor="purple"
							>
								Save
							</EdButton>
							<Button
								disabled={uploading}
								onClick={() => setCropSrc(undefined)}
								color="warning"
							>
								Cancel
							</Button>
						</DialogActions>
					</CropDialogWrapper>
				</Dialog>
			)}
		</>
	);
};

export default AssetUploadButton;

const CropDialogWrapper = styled.div`
	max-width: 100%;
	padding: 1rem 1rem 0;
`;
